/**
 * Predictive Behavioral Forecasting
 * ---------------------------------------------------------------
 * Live forecasting console — consumes asset_predictions joined
 * against content_assets to surface:
 *   • Emerging Behavioral Winners
 *   • Predicted Platform Dominance
 *   • Predicted Archetype Drift
 *   • Forecasted Escalation Paths
 *   • Predicted Hook Fatigue
 *   • Behavioral Saturation Detection
 *
 * Same forensic / cyan-amber operational aesthetic. No analytics.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Crosshair, Activity, AlertTriangle, TrendingUp, TrendingDown,
  Radar,
} from "lucide-react";
import {
  ASSET_PREDICTIONS_UPDATED_EVENT,
} from "@/lib/predictionEngine";
import { CONTENT_ASSETS_CHANGED_EVENT } from "@/lib/contentTelemetry";

interface PredRow {
  asset_id: string;
  predicted_virality_score: number;
  predicted_replay_rate: number;
  predicted_completion_rate: number;
  predicted_save_rate: number;
  predicted_export_rate: number;
  predicted_engagement_score: number;
  predicted_platform_fit: number;
  hook_efficiency: number;
  emotional_escalation_strength: number;
  dominant_signal: string | null;
  confidence: number;
  created_at: string;
}

interface AssetLite {
  id: string;
  archetype: string;
  format: string;
  platform: string;
  hook: string | null;
  category: string;
  created_at: string;
}

const titleCase = (s: string) =>
  (s || "—").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const avg = (xs: number[]) =>
  xs.length ? xs.reduce((s, n) => s + n, 0) / xs.length : 0;

const trendOf = (recent: number[], older: number[]) => {
  const r = avg(recent), o = avg(older);
  return Math.round(r - o);
};

function escalationOf(a: AssetLite): string {
  const k = (a.category || "").toLowerCase();
  if (k.includes("guilt")) return "guilt";
  if (k.includes("shame")) return "shame";
  if (k.includes("withdraw") || k.includes("silent")) return "withdrawal";
  if (k.includes("defens") || k.includes("blame")) return "defensive";
  return "emotional";
}

export default function PredictiveBehavioralForecasting() {
  const [preds, setPreds] = useState<PredRow[]>([]);
  const [assets, setAssets] = useState<Record<string, AssetLite>>({});
  const [loading, setLoading] = useState(true);
  const tickRef = useRef(0);
  const [, force] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: p }, { data: a }] = await Promise.all([
      (supabase as any)
        .from("asset_predictions")
        .select("asset_id, predicted_virality_score, predicted_replay_rate, predicted_completion_rate, predicted_save_rate, predicted_export_rate, predicted_engagement_score, predicted_platform_fit, hook_efficiency, emotional_escalation_strength, dominant_signal, confidence, created_at")
        .order("created_at", { ascending: false })
        .limit(800),
      (supabase as any)
        .from("content_assets")
        .select("id, archetype, format, platform, hook, category, created_at")
        .order("created_at", { ascending: false })
        .limit(800),
    ]);
    setPreds((p ?? []) as PredRow[]);
    const map: Record<string, AssetLite> = {};
    ((a ?? []) as AssetLite[]).forEach((row) => { map[row.id] = row; });
    setAssets(map);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const handler = () => { void load(); };
    window.addEventListener(ASSET_PREDICTIONS_UPDATED_EVENT, handler);
    window.addEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
    return () => {
      window.removeEventListener(ASSET_PREDICTIONS_UPDATED_EVENT, handler);
      window.removeEventListener(CONTENT_ASSETS_CHANGED_EVENT, handler);
    };
  }, [load]);

  // Pulse tick
  useEffect(() => {
    const id = window.setInterval(() => {
      tickRef.current += 1;
      force((v) => v + 1);
    }, 3500);
    return () => window.clearInterval(id);
  }, []);

  // Join preds with their asset features
  const merged = useMemo(() => {
    return preds
      .map((p) => ({ p, a: assets[p.asset_id] }))
      .filter((x): x is { p: PredRow; a: AssetLite } => !!x.a);
  }, [preds, assets]);

  // Recent vs older window for drift detection
  const half = Math.floor(merged.length / 2);
  const recent = merged.slice(0, half);
  const older = merged.slice(half);

  // ── Emerging Behavioral Winners ────────────────────────────
  const winners = useMemo(() => {
    const groups = new Map<string, { v: number[]; r: number[]; sample: number; signal: string }>();
    merged.forEach(({ p, a }) => {
      const key = `${a.archetype}|${a.format}|${escalationOf(a)}`;
      const g = groups.get(key) ?? { v: [], r: [], sample: 0, signal: p.dominant_signal ?? "" };
      g.v.push(p.predicted_virality_score);
      g.r.push(p.predicted_replay_rate);
      g.sample += 1;
      groups.set(key, g);
    });
    return Array.from(groups.entries())
      .map(([k, g]) => {
        const [archetype, format, escalation] = k.split("|");
        return {
          archetype, format, escalation, sample: g.sample,
          virality: avg(g.v), replay: avg(g.r), signal: g.signal,
        };
      })
      .filter((x) => x.sample >= 2)
      .sort((a, b) => b.virality - a.virality)
      .slice(0, 8);
  }, [merged]);

  // ── Predicted Platform Dominance ───────────────────────────
  const platforms = useMemo(() => {
    const m = new Map<string, { fit: number[]; v: number[]; sample: number }>();
    merged.forEach(({ p, a }) => {
      const k = a.platform || "multi";
      const g = m.get(k) ?? { fit: [], v: [], sample: 0 };
      g.fit.push(p.predicted_platform_fit);
      g.v.push(p.predicted_virality_score);
      g.sample += 1;
      m.set(k, g);
    });
    return Array.from(m.entries())
      .map(([platform, g]) => ({
        platform, sample: g.sample,
        avg_fit: avg(g.fit), avg_virality: avg(g.v),
      }))
      .sort((a, b) => b.avg_virality - a.avg_virality);
  }, [merged]);

  // ── Predicted Archetype Drift ──────────────────────────────
  const archetypeDrift = useMemo(() => {
    const recentMap = new Map<string, number[]>();
    const olderMap = new Map<string, number[]>();
    recent.forEach(({ p, a }) => {
      const arr = recentMap.get(a.archetype) ?? [];
      arr.push(p.predicted_virality_score);
      recentMap.set(a.archetype, arr);
    });
    older.forEach(({ p, a }) => {
      const arr = olderMap.get(a.archetype) ?? [];
      arr.push(p.predicted_virality_score);
      olderMap.set(a.archetype, arr);
    });
    const keys = new Set([...recentMap.keys(), ...olderMap.keys()]);
    return Array.from(keys)
      .map((k) => ({
        archetype: k,
        delta: trendOf(recentMap.get(k) ?? [], olderMap.get(k) ?? []),
        recent: avg(recentMap.get(k) ?? []),
        sample: (recentMap.get(k) ?? []).length + (olderMap.get(k) ?? []).length,
      }))
      .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
      .slice(0, 8);
  }, [recent, older]);

  // ── Forecasted Escalation Paths ────────────────────────────
  const escalationForecast = useMemo(() => {
    const m = new Map<string, { v: number[]; r: number[]; c: number[]; sample: number }>();
    merged.forEach(({ p, a }) => {
      const k = escalationOf(a);
      const g = m.get(k) ?? { v: [], r: [], c: [], sample: 0 };
      g.v.push(p.predicted_virality_score);
      g.r.push(p.predicted_replay_rate);
      g.c.push(p.predicted_completion_rate);
      g.sample += 1;
      m.set(k, g);
    });
    return Array.from(m.entries())
      .map(([path, g]) => ({
        path, sample: g.sample,
        avg_virality: avg(g.v),
        avg_replay: avg(g.r),
        avg_completion: avg(g.c),
      }))
      .sort((a, b) => b.avg_virality - a.avg_virality);
  }, [merged]);

  // ── Predicted Hook Fatigue ─────────────────────────────────
  const hookFatigue = useMemo(() => {
    const norm = (h: string) => h.trim().toLowerCase().slice(0, 60);
    const m = new Map<string, { uses: number; lastEff: number[]; firstEff: number[] }>();
    merged.forEach(({ p, a }, i) => {
      if (!a.hook) return;
      const k = norm(a.hook);
      const g = m.get(k) ?? { uses: 0, lastEff: [], firstEff: [] };
      g.uses += 1;
      // first half of merged = recent → firstEff (most recent uses lower position)
      if (i < half) g.lastEff.push(p.hook_efficiency);
      else          g.firstEff.push(p.hook_efficiency);
      m.set(k, g);
    });
    return Array.from(m.entries())
      .filter(([, g]) => g.uses >= 3)
      .map(([phrase, g]) => ({
        phrase,
        uses: g.uses,
        delta: trendOf(g.lastEff, g.firstEff),
        recent_eff: avg(g.lastEff),
      }))
      .filter((x) => x.delta < 0 || x.uses >= 5)
      .sort((a, b) => a.delta - b.delta)
      .slice(0, 8);
  }, [merged, half]);

  // ── Behavioral Saturation Warnings ─────────────────────────
  const saturationWarnings = useMemo(() => {
    const out: { tag: string; message: string; tone: "amber" | "red" | "cyan" }[] = [];

    // Format saturation: any format dominating > 55% of recent batch
    const fmtTotals = new Map<string, number>();
    recent.forEach(({ a }) => fmtTotals.set(a.format, (fmtTotals.get(a.format) ?? 0) + 1));
    const total = recent.length || 1;
    fmtTotals.forEach((n, fmt) => {
      const share = (n / total) * 100;
      if (share > 55) {
        out.push({
          tag: `FORMAT SATURATION`,
          message: `${titleCase(fmt)} occupies ${Math.round(share)}% of recent generation — diversify structures.`,
          tone: "amber",
        });
      }
    });

    // Escalation burnout: declining replay across recent vs older
    escalationForecast.forEach((e) => {
      const recR = avg(recent.filter(({ a }) => escalationOf(a) === e.path).map(({ p }) => p.predicted_replay_rate));
      const oldR = avg(older.filter(({ a }) => escalationOf(a) === e.path).map(({ p }) => p.predicted_replay_rate));
      const d = Math.round(recR - oldR);
      if (d <= -8 && e.sample >= 4) {
        out.push({
          tag: `ESCALATION BURNOUT`,
          message: `${titleCase(e.path)} replay velocity declining ${d}% — rotate pacing.`,
          tone: "red",
        });
      }
    });

    // Platform drift winners
    platforms.forEach((pl) => {
      if (pl.avg_virality >= 72 && pl.sample >= 4) {
        out.push({
          tag: `PLATFORM DOMINANCE`,
          message: `${titleCase(pl.platform)} forecasting ${Math.round(pl.avg_virality)} virality — amplify allocation.`,
          tone: "cyan",
        });
      }
    });

    // Hook fatigue → top warning
    hookFatigue.slice(0, 2).forEach((h) => {
      out.push({
        tag: `HOOK FATIGUE`,
        message: `"${h.phrase.slice(0, 48)}…" — efficiency Δ ${h.delta} across ${h.uses} uses.`,
        tone: h.delta <= -10 ? "red" : "amber",
      });
    });

    return out.slice(0, 8);
  }, [recent, older, escalationForecast, platforms, hookFatigue]);

  // Live status line
  const liveStatus = useMemo(() => {
    const lines = [
      "Behavioral forecast convergence...",
      "Escalation drift detected...",
      "Platform fit re-weighting...",
      "Hook fatigue scan complete...",
      "Saturation envelope updated...",
    ];
    return lines[tickRef.current % lines.length];
  }, [tickRef.current]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Crosshair className="w-4 h-4 text-cyan" />
          Predictive Behavioral Forecasting
          <Badge variant="outline" className="text-[10px] uppercase tracking-wider border-amber-400/40 text-amber-400 font-mono">
            Forecast Engine
          </Badge>
          <span className="ml-auto inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono text-cyan/80">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan animate-pulse" />
            {merged.length} forecasts
          </span>
        </CardTitle>
        <p className="text-[11px] font-mono text-muted-foreground tracking-wider">
          {liveStatus}
        </p>
      </CardHeader>

      <CardContent className="space-y-6">
        {loading ? (
          <div className="text-center py-8 text-xs font-mono text-muted-foreground">
            Initializing forecast layer…
          </div>
        ) : merged.length === 0 ? (
          <div className="text-center py-8 text-xs font-mono text-muted-foreground">
            No predictions yet. Generate a bundle in Admin → Viral to seed the forecast.
          </div>
        ) : (
          <>
            {/* Saturation warnings (top — operator alerts) */}
            {saturationWarnings.length > 0 && (
              <div className="rounded-md border border-amber-400/30 bg-amber-400/[0.04] p-3 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-[10px] uppercase tracking-wider font-mono text-amber-400">
                    Behavioral Saturation Detection
                  </span>
                </div>
                <div className="space-y-1">
                  {saturationWarnings.map((w, i) => (
                    <div key={i} className="flex items-start gap-2 text-[11px] font-mono">
                      <span
                        className={`px-1.5 py-0.5 rounded border text-[9px] tracking-wider shrink-0 ${
                          w.tone === "red"   ? "border-red-400/50 text-red-300 bg-red-400/10" :
                          w.tone === "amber" ? "border-amber-400/50 text-amber-300 bg-amber-400/10" :
                                               "border-cyan/50 text-cyan bg-cyan/10"
                        }`}
                      >
                        {w.tag}
                      </span>
                      <span className="text-foreground/80 leading-relaxed">{w.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Two-column grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Emerging Winners */}
              <Section icon={<TrendingUp className="w-3.5 h-3.5 text-emerald-300" />} title="Emerging Behavioral Winners">
                <div className="space-y-1.5">
                  {winners.map((w, i) => (
                    <div key={i} className="flex items-center gap-3 px-2.5 py-1.5 rounded-sm border border-glass-border bg-charcoal-deep/40">
                      <span className="text-[10px] font-mono text-cyan/70 tabular-nums w-6">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[11px] font-mono text-foreground truncate">
                          {titleCase(w.archetype)} · {titleCase(w.format)} · {titleCase(w.escalation)}
                        </div>
                        {w.signal && (
                          <div className="text-[9px] uppercase tracking-wider text-cyan/70 font-mono">
                            SIGNAL: {w.signal}
                          </div>
                        )}
                      </div>
                      <span className="text-[11px] font-mono text-amber-300 tabular-nums">
                        {Math.round(w.virality)}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Platform Dominance */}
              <Section icon={<Radar className="w-3.5 h-3.5 text-cyan" />} title="Predicted Platform Dominance">
                <div className="space-y-2">
                  {platforms.map((pl) => (
                    <div key={pl.platform} className="space-y-1">
                      <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider font-mono">
                        <span className="text-foreground">{titleCase(pl.platform)}</span>
                        <span className="text-cyan tabular-nums">{Math.round(pl.avg_virality)} V · {Math.round(pl.avg_fit)}% FIT</span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-charcoal-deep border border-glass-border overflow-hidden">
                        <div
                          className="h-full bg-cyan transition-all duration-700"
                          style={{ width: `${Math.min(100, pl.avg_virality)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Archetype Drift */}
              <Section icon={<Activity className="w-3.5 h-3.5 text-amber-400" />} title="Predicted Archetype Drift">
                <div className="space-y-1.5">
                  {archetypeDrift.length === 0 ? (
                    <div className="text-[11px] font-mono text-muted-foreground">Insufficient sample for drift analysis.</div>
                  ) : archetypeDrift.map((a) => (
                    <div key={a.archetype} className="flex items-center gap-2 text-[11px] font-mono">
                      <span className="text-foreground flex-1 truncate">{titleCase(a.archetype)}</span>
                      <span className={`tabular-nums ${
                        a.delta > 0 ? "text-emerald-300" :
                        a.delta < 0 ? "text-red-300" : "text-muted-foreground"
                      }`}>
                        {a.delta > 0 ? <TrendingUp className="w-3 h-3 inline mr-0.5" /> :
                         a.delta < 0 ? <TrendingDown className="w-3 h-3 inline mr-0.5" /> : null}
                        {a.delta >= 0 ? "+" : ""}{a.delta}
                      </span>
                      <span className="text-cyan tabular-nums w-10 text-right">{Math.round(a.recent)}</span>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Escalation Forecast */}
              <Section icon={<Crosshair className="w-3.5 h-3.5 text-cyan" />} title="Forecasted Escalation Paths">
                <div className="space-y-2">
                  {escalationForecast.map((e) => (
                    <div key={e.path} className="space-y-1">
                      <div className="flex items-baseline justify-between text-[10px] uppercase tracking-wider font-mono">
                        <span className="text-foreground">{titleCase(e.path)}</span>
                        <span className="text-amber-300 tabular-nums">
                          V {Math.round(e.avg_virality)} · R {Math.round(e.avg_replay)}% · C {Math.round(e.avg_completion)}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-charcoal-deep border border-glass-border overflow-hidden">
                        <div
                          className="h-full bg-amber-400/80 transition-all duration-700"
                          style={{ width: `${Math.min(100, e.avg_virality)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Section>
            </div>

            {/* Hook Fatigue */}
            <Section icon={<TrendingDown className="w-3.5 h-3.5 text-red-300" />} title="Predicted Hook Fatigue">
              {hookFatigue.length === 0 ? (
                <div className="text-[11px] font-mono text-muted-foreground">No fatigue detected — hook diversity within tolerance.</div>
              ) : (
                <ScrollArea className="max-h-[200px]">
                  <div className="space-y-1">
                    {hookFatigue.map((h, i) => (
                      <div key={i} className="flex items-start gap-3 px-2.5 py-1.5 rounded-sm border border-glass-border bg-charcoal-deep/40">
                        <span className={`text-[10px] font-mono tabular-nums px-1.5 py-0.5 rounded border shrink-0 ${
                          h.delta <= -10 ? "border-red-400/50 text-red-300 bg-red-400/10" :
                                            "border-amber-400/50 text-amber-300 bg-amber-400/10"
                        }`}>
                          {h.delta >= 0 ? "+" : ""}{h.delta}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] text-foreground/90 italic truncate">"{h.phrase}…"</div>
                          <div className="text-[9px] uppercase tracking-wider font-mono text-muted-foreground">
                            {h.uses} uses · recent eff {Math.round(h.recent_eff)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </Section>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ icon, title, children }: {
  icon: React.ReactNode; title: string; children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-glass-border bg-charcoal-deep/30 p-3 space-y-3">
      <div className="flex items-center gap-2">
        {icon}
        <span className="text-[10px] uppercase tracking-wider font-mono text-cyan">{title}</span>
      </div>
      {children}
    </div>
  );
}
